import { apiService } from '../../api-base'
import {
  CostingApiResponse,
  CostingCatalogProductResponseDTO,
  CostingFilters,
  CostingCatalogProductsParams,
  CostingListParams,
  CostingMaterialResponseDTO,
  CostingMaterialUpsertPayload,
  CostingMessageResponse,
  CostingOperationResponseDTO,
  CostingOperationUpsertPayload,
  CostingProductResponseDTO,
  CostingProductUpsertPayload,
  CostingReceiptResponseDTO,
  CostingReceiptUpsertPayload,
  CostingSnapshotResponseDTO,
  CostingSpecificationResponseDTO,
  CostingSpecificationUpsertPayload,
} from './types'

export class CostingService {
  static getFilters(): Promise<CostingApiResponse<CostingFilters>> {
    return apiService.get<CostingApiResponse<CostingFilters>>(
      '/costing/filters',
    )
  }

  static getProducts(
    params?: CostingListParams,
  ): Promise<CostingApiResponse<CostingProductResponseDTO[]>> {
    return apiService.get<CostingApiResponse<CostingProductResponseDTO[]>>(
      '/costing/products',
      {
        params,
      },
    )
  }

  static getCatalogProducts(
    params?: CostingCatalogProductsParams,
  ): Promise<CostingApiResponse<CostingCatalogProductResponseDTO[]>> {
    return apiService.get<
      CostingApiResponse<CostingCatalogProductResponseDTO[]>
    >('/costing/catalog-products', { params })
  }

  static getProduct(
    id: string,
  ): Promise<CostingApiResponse<CostingProductResponseDTO>> {
    return apiService.get<CostingApiResponse<CostingProductResponseDTO>>(
      `/costing/products/${id}`,
    )
  }

  static createProduct(
    payload: CostingProductUpsertPayload,
  ): Promise<CostingApiResponse<CostingProductResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingProductResponseDTO>>(
      '/costing/products',
      payload,
    )
  }

  static updateProduct(
    id: string,
    payload: CostingProductUpsertPayload,
  ): Promise<CostingApiResponse<CostingProductResponseDTO>> {
    return apiService.put<CostingApiResponse<CostingProductResponseDTO>>(
      `/costing/products/${id}`,
      payload,
    )
  }

  static deleteProduct(
    id: string,
  ): Promise<CostingApiResponse<CostingMessageResponse>> {
    return apiService.delete<CostingApiResponse<CostingMessageResponse>>(
      `/costing/products/${id}`,
    )
  }

  static getProductCost(
    id: string,
  ): Promise<CostingApiResponse<CostingSnapshotResponseDTO | null>> {
    return apiService.get<
      CostingApiResponse<CostingSnapshotResponseDTO | null>
    >(`/costing/products/${id}/cost`)
  }

  static createProductSpecification(
    productId: string,
    payload: CostingSpecificationUpsertPayload,
  ): Promise<CostingApiResponse<CostingSpecificationResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingSpecificationResponseDTO>>(
      `/costing/products/${productId}/specifications`,
      payload,
    )
  }

  static getSpecification(
    id: string,
  ): Promise<CostingApiResponse<CostingSpecificationResponseDTO>> {
    return apiService.get<CostingApiResponse<CostingSpecificationResponseDTO>>(
      `/costing/specifications/${id}`,
    )
  }

  static updateSpecification(
    id: string,
    payload: CostingSpecificationUpsertPayload,
  ): Promise<CostingApiResponse<CostingSpecificationResponseDTO>> {
    return apiService.put<CostingApiResponse<CostingSpecificationResponseDTO>>(
      `/costing/specifications/${id}`,
      payload,
    )
  }

  static deleteSpecification(
    id: string,
  ): Promise<CostingApiResponse<CostingMessageResponse>> {
    return apiService.delete<CostingApiResponse<CostingMessageResponse>>(
      `/costing/specifications/${id}`,
    )
  }

  static calculateSpecification(
    id: string,
  ): Promise<CostingApiResponse<CostingSpecificationResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingSpecificationResponseDTO>>(
      `/costing/specifications/${id}/calculate`,
    )
  }

  static publishSpecification(
    id: string,
  ): Promise<CostingApiResponse<CostingSnapshotResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingSnapshotResponseDTO>>(
      `/costing/specifications/${id}/publish`,
    )
  }

  static getMaterials(
    params?: CostingListParams,
  ): Promise<CostingApiResponse<CostingMaterialResponseDTO[]>> {
    return apiService.get<CostingApiResponse<CostingMaterialResponseDTO[]>>(
      '/costing/materials',
      {
        params,
      },
    )
  }

  static getMaterial(
    id: string,
  ): Promise<CostingApiResponse<CostingMaterialResponseDTO>> {
    return apiService.get<CostingApiResponse<CostingMaterialResponseDTO>>(
      `/costing/materials/${id}`,
    )
  }

  static createMaterial(
    payload: CostingMaterialUpsertPayload,
  ): Promise<CostingApiResponse<CostingMaterialResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingMaterialResponseDTO>>(
      '/costing/materials',
      payload,
    )
  }

  static updateMaterial(
    id: string,
    payload: CostingMaterialUpsertPayload,
  ): Promise<CostingApiResponse<CostingMaterialResponseDTO>> {
    return apiService.put<CostingApiResponse<CostingMaterialResponseDTO>>(
      `/costing/materials/${id}`,
      payload,
    )
  }

  static deleteMaterial(
    id: string,
  ): Promise<CostingApiResponse<CostingMessageResponse>> {
    return apiService.delete<CostingApiResponse<CostingMessageResponse>>(
      `/costing/materials/${id}`,
    )
  }

  static getMaterialReceipts(
    materialId: string,
  ): Promise<CostingApiResponse<CostingReceiptResponseDTO[]>> {
    return apiService.get<CostingApiResponse<CostingReceiptResponseDTO[]>>(
      `/costing/materials/${materialId}/receipts`,
    )
  }

  static createReceipt(
    materialId: string,
    payload: CostingReceiptUpsertPayload,
  ): Promise<CostingApiResponse<CostingReceiptResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingReceiptResponseDTO>>(
      `/costing/materials/${materialId}/receipts`,
      payload,
    )
  }

  static updateReceipt(
    materialId: string,
    receiptId: string,
    payload: CostingReceiptUpsertPayload,
  ): Promise<CostingApiResponse<CostingReceiptResponseDTO>> {
    return apiService.put<CostingApiResponse<CostingReceiptResponseDTO>>(
      `/costing/materials/${materialId}/receipts/${receiptId}`,
      payload,
    )
  }

  static deleteReceipt(
    materialId: string,
    receiptId: string,
  ): Promise<CostingApiResponse<CostingMessageResponse>> {
    return apiService.delete<CostingApiResponse<CostingMessageResponse>>(
      `/costing/materials/${materialId}/receipts/${receiptId}`,
    )
  }

  static getOperations(
    params?: CostingListParams,
  ): Promise<CostingApiResponse<CostingOperationResponseDTO[]>> {
    return apiService.get<CostingApiResponse<CostingOperationResponseDTO[]>>(
      '/costing/operations',
      {
        params,
      },
    )
  }

  static getOperation(
    id: string,
  ): Promise<CostingApiResponse<CostingOperationResponseDTO>> {
    return apiService.get<CostingApiResponse<CostingOperationResponseDTO>>(
      `/costing/operations/${id}`,
    )
  }

  static createOperation(
    payload: CostingOperationUpsertPayload,
  ): Promise<CostingApiResponse<CostingOperationResponseDTO>> {
    return apiService.post<CostingApiResponse<CostingOperationResponseDTO>>(
      '/costing/operations',
      payload,
    )
  }

  static updateOperation(
    id: string,
    payload: CostingOperationUpsertPayload,
  ): Promise<CostingApiResponse<CostingOperationResponseDTO>> {
    return apiService.put<CostingApiResponse<CostingOperationResponseDTO>>(
      `/costing/operations/${id}`,
      payload,
    )
  }

  static deleteOperation(
    id: string,
  ): Promise<CostingApiResponse<CostingMessageResponse>> {
    return apiService.delete<CostingApiResponse<CostingMessageResponse>>(
      `/costing/operations/${id}`,
    )
  }
}
